import { useEffect, useState, useMemo } from "react";
import api, { errMsg } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Plus, Trash2, X, Wand2, Users, MapPin, Truck, Pencil, ChevronDown, ChevronUp, Search, Clock, Route as RouteIcon, Map as MapIcon, Loader2, MessageCircle, Smartphone, Share2 } from "lucide-react";
import { toast } from "sonner";
import { RouteMapModal } from "@/components/app/RouteMapModal";
import { CompanyRoutesMapModal } from "@/components/app/CompanyRoutesMapModal";
import { StopBuilderMapModal } from "@/components/app/StopBuilderMapModal";
import { hasGoogle, googleGeocode } from "@/lib/googleMaps";
import ExportButtons from "@/components/app/ExportButtons";

export default function TransportRoutesPage() {
  const [routes, setRoutes] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [showAuto, setShowAuto] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [editing, setEditing] = useState(null);
  const [autoForm, setAutoForm] = useState({
    company_name: "", direction: "pickup", shift: "", capacities: "16,27", stop_merge_m: 250, districts: [],
  });
  const [destQuery, setDestQuery] = useState("");
  const [destResults, setDestResults] = useState([]);
  const [dest, setDest] = useState(null); // {lat, lng, label}
  const [destSearching, setDestSearching] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [mapRoute, setMapRoute] = useState(null);
  const [companyMap, setCompanyMap] = useState(null); // firma adı — toplu harita
  const [showStopBuilder, setShowStopBuilder] = useState(false);
  const [companySettings, setCompanySettings] = useState({}); // {firma: {work_start, buffer_min}}
  const [workStartDraft, setWorkStartDraft] = useState({}); // {firma: "08:00"} — input taslakları
  const [manualForm, setManualForm] = useState({
    name: "", company_name: "", direction: "pickup", shift: "", vehicle_id: "", driver_id: "",
    start_point: "", end_point: "", personnel_ids: [], estimated_km: 0, notes: ""
  });

  const load = async () => {
    try {
      const [r, p, v, d, cs] = await Promise.all([
        api.get("/transport-routes"),
        api.get("/customer-personnel"),
        api.get("/vehicles"),
        api.get("/drivers"),
        api.get("/company-settings"),
      ]);
      setRoutes(r.data); setPersonnel(p.data); setVehicles(v.data); setDrivers(d.data);
      setCompanySettings(Object.fromEntries(cs.data.map(x => [x.company_name, x])));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const companies = useMemo(() => [...new Set(personnel.map(p => p.company_name))].filter(Boolean).sort(), [personnel]);
  const routeCompanies = useMemo(() => [...new Set(routes.map(r => r.company_name))].filter(Boolean).sort(), [routes]);

  const filteredRoutes = companyFilter ? routes.filter(r => r.company_name === companyFilter) : routes;
  const groupedByCompany = filteredRoutes.reduce((acc, r) => {
    (acc[r.company_name] = acc[r.company_name] || []).push(r);
    return acc;
  }, {});

  const personnelById = Object.fromEntries(personnel.map(p => [p.id, p]));
  const vehicleById = Object.fromEntries(vehicles.map(v => [v.id, v]));
  const driverById = Object.fromEntries(drivers.map(d => [d.id, d]));

  const movePersonnel = async (routeId, pid, targetId) => {
    try {
      const { data } = await api.post(`/transport-routes/${routeId}/move-personnel`, { personnel_id: pid, target_route_id: targetId });
      toast.success(`Personel taşındı${data.walk_m != null ? ` · yeni durağa yürüme ${data.walk_m} m` : ""}`);
      if (data.warning) toast.warning(data.warning, { duration: 7000 });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const addPersonnelToRoute = async (pid, routeId) => {
    try {
      const { data } = await api.post(`/transport-routes/${routeId}/add-personnel`, { personnel_id: pid });
      toast.success(`Personel güzergaha eklendi · yeni durak oluşturuldu (yürüme ${data.walk_m} m)${data.estimated_km ? ` · ${data.estimated_km} km` : ""}`);
      if (data.warning) toast.warning(data.warning, { duration: 7000 });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const createRouteOnMap = async (companyName, payload) => {
    try {
      const { data } = await api.post("/transport-routes/create-on-map", { company_name: companyName, ...payload });
      toast.success(`"${data.route.name}" oluşturuldu · ${data.route.stops.length} durak · ${data.route.estimated_km} km`);
      load();
    } catch (err) { toast.error(errMsg(err)); throw err; }
  };

  const MoveSelect = ({ route, pid }) => {
    const targets = routes.filter(x => x.id !== route.id && x.company_name === route.company_name);
    if (targets.length === 0) return null;
    return (
      <select
        data-testid={`route-move-${route.id}-${pid}`}
        value=""
        onChange={e => e.target.value && movePersonnel(route.id, pid, e.target.value)}
        className="text-[10px] border border-[#E5E5E5] bg-white px-1 py-0.5 cursor-pointer text-[#0020C2] font-semibold shrink-0"
        title="Bu personeli başka güzergaha taşı"
      >
        <option value="">Taşı →</option>
        {targets.map(x => <option key={x.id} value={x.id}>{x.name.slice(0, 45)}</option>)}
      </select>
    );
  };

  const calcTimes = async (company) => {
    const ws = workStartDraft[company] ?? companySettings[company]?.work_start ?? "";
    if (!ws) { toast.error("Önce iş başlangıç saatini girin (örn. 08:00)"); return; }
    try {
      await api.put(`/company-settings/${encodeURIComponent(company)}`, { work_start: ws, buffer_min: companySettings[company]?.buffer_min ?? 10 });
      const { data } = await api.post("/transport-routes/compute-times", { company_name: company });
      toast.success(`${data.updated} güzergahta saatler hesaplandı — işbaşı ${data.work_start}, varış ${data.arrival_time}`);
      if (data.skipped?.length > 0) toast.info(`Atlanan (haritasız/bırakma): ${data.skipped.slice(0, 3).join(", ")}${data.skipped.length > 3 ? "..." : ""}`, { duration: 6000 });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const moveStop = async (routeId, stopIndex, lat, lng) => {
    try {
      const { data } = await api.post(`/transport-routes/${routeId}/move-stop`, { stop_index: stopIndex, lat, lng });
      toast.success(`Durak taşındı — ${data.estimated_km} km · yürüme mesafeleri ve saatler güncellendi`);
      if (mapRoute?.id === routeId) setMapRoute(data.route);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const renameRoute = async (routeId, name) => {
    try {
      await api.patch(`/transport-routes/${routeId}/rename`, { name });
      toast.success("Güzergah adı güncellendi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const shareLink = async (payload, title) => {
    try {
      const { data } = await api.post("/transport-routes/share", payload);
      const url = `${window.location.origin}/paylas/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Paylaşım linki panoya kopyalandı"); } catch { toast.info(url, { duration: 10000 }); }
      window.open(`https://wa.me/?text=${encodeURIComponent(`*KESKİNTUR GÜZERGAH PLANI*\n${title}\n${url}`)}`, "_blank");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const quickAssign = async (routeId, field, value) => {
    try {
      await api.patch(`/transport-routes/${routeId}/assign`, { [field]: value });
      toast.success(field === "vehicle_id" ? "Araç atandı" : "Şoför atandı");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const sendWhatsApp = (r) => {
    const driver = driverById[r.driver_id];
    if (!driver) { toast.error("Önce şoför atayın"); return; }
    const phone = (driver.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error(`${driver.name} için telefon kayıtlı değil — Şoförler sayfasından ekleyin`); return; }
    const intl = phone.startsWith("90") ? phone : phone.startsWith("0") ? "9" + phone : "90" + phone;

    const lines = [`*KESKİNTUR SERVİS GÖREVİ*`, ``, `Güzergah: ${r.name}`, `Firma: ${r.company_name}`];
    if (r.estimated_km > 0) lines.push(`Mesafe: ${r.estimated_km} km${r.duration_min ? ` · ~${r.duration_min} dk` : ""}`);
    lines.push(`Personel: ${(r.personnel_ids || []).length} kişi`, ``, `*DURAKLAR:*`);
    if (r.stops?.length > 0) {
      r.stops.forEach((s, i) => {
        const names = (s.personnel || []).map(p => p.pickup_time ? `${p.name} (${p.pickup_time})` : p.name).join(", ");
        lines.push(`${i + 1}) ${names}`);
      });
    } else {
      (r.personnel_ids || []).forEach((pid, i) => {
        const p = personnelById[pid];
        if (p) lines.push(`${i + 1}) ${p.first_name} ${p.last_name}${p.pickup_time ? ` (${p.pickup_time})` : ""} — ${p.district || p.address || ""}`);
      });
    }
    if (r.dest?.label || r.end_point) lines.push(``, `Varış: ${r.dest?.label || r.end_point}`);

    // Google Maps navigasyon linki (duraklar sıralı)
    if (r.stops?.length > 0 && r.dest) {
      const pts = r.stops.map(s => `${s.lat},${s.lng}`);
      const origin = pts[0];
      const waypoints = pts.slice(1, 9).join("|");
      const destination = `${r.dest.lat},${r.dest.lng}`;
      lines.push(``, `Navigasyon: https://www.google.com/maps/dir/?api=1&origin=${origin}${waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : ""}&destination=${destination}&travelmode=driving`);
    }
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  const sendTaskLink = async (r) => {
    const driver = driverById[r.driver_id];
    if (!driver) { toast.error("Önce şoför atayın"); return; }
    try {
      const { data } = await api.post(`/drivers/${r.driver_id}/portal-token`);
      const url = `${window.location.origin}/gorev/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Görev linki panoya kopyalandı"); } catch { /* pano izni yok */ }
      const phone = (driver.phone || "").replace(/\D/g, "");
      if (phone) {
        const intl = phone.startsWith("90") ? phone : phone.startsWith("0") ? "9" + phone : "90" + phone;
        const msg = `*KESKİNTUR GÖREV EKRANI*\n\nMerhaba ${driver.name}, günün güzergah ve durak listenize bu linkten ulaşabilirsiniz:\n${url}`;
        window.open(`https://wa.me/${intl}?text=${encodeURIComponent(msg)}`, "_blank");
      } else {
        toast.info(`${driver.name} için telefon kayıtlı değil — linki elle paylaşın: ${url}`, { duration: 9000 });
      }
    } catch (err) { toast.error(errMsg(err)); }
  };

  const searchDest = async () => {
    if (destQuery.trim().length < 3) { toast.error("En az 3 karakter girin"); return; }
    setDestSearching(true);
    setDestResults([]);
    // Önce Google (nokta atışı), bulunamazsa ücretsiz OSM
    if (hasGoogle()) {
      try {
        const res = await googleGeocode(destQuery.trim());
        if (res.length > 0) {
          setDestResults(res);
          setDest({ lat: res[0].lat, lng: res[0].lng, label: res[0].label });
          setDestSearching(false);
          return;
        }
      } catch (e) {
        const msg = String(e.message || e);
        if (msg.startsWith("GOOGLE_AUTH")) toast.error(msg.replace("GOOGLE_AUTH: ", ""), { duration: 10000 });
      }
    }
    try {
      const { data } = await api.get("/geocode/search", { params: { q: destQuery.trim() } });
      setDestResults(data.results);
      if (data.results.length === 0) toast.info("Adres bulunamadı — daha genel yazın");
      else setDest({ lat: data.results[0].lat, lng: data.results[0].lng, label: data.results[0].label });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setDestSearching(false); }
  };

  const runAuto = async () => {
    if (!autoForm.company_name) { toast.error("Firma seçin"); return; }
    if (!dest) { toast.error("İşyeri (hedef) adresini arayıp seçin"); return; }
    const caps = autoForm.capacities.split(",").map(x => parseInt(x.trim(), 10)).filter(x => x > 0);
    if (caps.length === 0) { toast.error("Araç kapasitelerini girin (örn: 16,27)"); return; }
    setPlanning(true);
    try {
      const { data } = await api.post("/transport-routes/auto-plan", {
        company_name: autoForm.company_name,
        direction: autoForm.direction,
        shift: autoForm.shift,
        capacities: caps,
        dest_lat: dest.lat, dest_lng: dest.lng, dest_label: dest.label,
        stop_merge_m: Number(autoForm.stop_merge_m) || 250,
        districts: autoForm.districts.length > 0 ? autoForm.districts : undefined,
      });
      toast.success(`${data.generated} güzergah oluşturuldu · ${data.total_personnel} personel · toplam ${data.total_km} km${data.skipped_no_location ? ` · ${data.skipped_no_location} personel konumsuz (atlandı)` : ""}`);
      if (data.skipped_far?.length > 0) {
        toast.warning(`${data.skipped_far.length} personel hedeften çok uzak konumlandığı için plana alınmadı — adresleri "Konumu Düzelt" ile kontrol edin`, { duration: 8000 });
      }
      setShowAuto(false); load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setPlanning(false); }
  };

  const openManual = (route = null) => {
    if (route) {
      setEditing(route);
      setManualForm({
        name: route.name || "", company_name: route.company_name || "",
        direction: route.direction || "pickup", shift: route.shift || "",
        vehicle_id: route.vehicle_id || "", driver_id: route.driver_id || "",
        start_point: route.start_point || "", end_point: route.end_point || "",
        personnel_ids: route.personnel_ids || [],
        estimated_km: route.estimated_km || 0, notes: route.notes || "",
        stops: route.stops || undefined, geometry: route.geometry || undefined,
        dest: route.dest || undefined, duration_min: route.duration_min || undefined,
      });
    } else {
      setEditing(null);
      setManualForm({
        name: "", company_name: "", direction: "pickup", shift: "", vehicle_id: "", driver_id: "",
        start_point: "", end_point: "", personnel_ids: [], estimated_km: 0, notes: ""
      });
    }
    setShowManual(true);
  };

  const openStopBuilder = () => {
    const ppl = manualForm.personnel_ids.map(id => personnelById[id]).filter(Boolean);
    if (ppl.length === 0) { toast.error("Önce personel seçin"); return; }
    if (!ppl.some(p => typeof p.lat === "number")) { toast.error("Seçilen personellerin hiçbiri konumlanmamış — önce 'Adresleri Haritada Konumla' yapın"); return; }
    setShowStopBuilder(true);
  };

  const applyMapData = (data) => {
    setManualForm(f => ({
      ...f,
      stops: data.stops, geometry: data.geometry, dest: data.dest,
      estimated_km: data.estimated_km, duration_min: data.duration_min,
      end_point: f.end_point || data.dest.label || "",
    }));
    toast.success(`Harita oluşturuldu: ${data.stops.length} durak · ${data.estimated_km} km · ~${data.duration_min} dk`);
  };

  const submitManual = async () => {
    if (!manualForm.name || !manualForm.company_name) { toast.error("Güzergah adı ve firma gerekli"); return; }
    const payload = { ...manualForm, estimated_km: Number(manualForm.estimated_km) || 0 };
    try {
      if (editing) await api.put(`/transport-routes/${editing.id}`, payload);
      else await api.post("/transport-routes", payload);
      toast.success("Kaydedildi");
      setShowManual(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const removeRoute = async (r) => {
    if (!window.confirm(`"${r.name}" güzergahı silinsin mi?`)) return;
    try { await api.delete(`/transport-routes/${r.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const toggleExpand = (id) => {
    const s = new Set(expanded);
    if (s.has(id)) s.delete(id); else s.add(id);
    setExpanded(s);
  };

  // ── Güzergah planı dışa aktarma (Excel/PDF) ──
  const dirText = (d) => (d === "pickup" ? "Alım" : "Bırakma");
  const exportSections = useMemo(() => {
    if (filteredRoutes.length === 0) return [];
    const summary = {
      name: "Özet",
      heading: "Güzergah Özeti",
      columns: [
        { key: "name", label: "Güzergah" },
        { key: "company_name", label: "Firma" },
        { key: "dir", label: "Yön", get: (r) => dirText(r.direction) },
        { key: "shift", label: "Vardiya" },
        { key: "vehicle", label: "Araç", get: (r) => vehicleById[r.vehicle_id]?.plate || "-" },
        { key: "driver", label: "Şoför", get: (r) => driverById[r.driver_id]?.name || "-" },
        { key: "driver_phone", label: "Şoför Tel", get: (r) => driverById[r.driver_id]?.phone || "-" },
        { key: "count", label: "Kişi", format: "number", get: (r) => r.personnel_ids?.length || 0 },
        { key: "stops", label: "Durak", format: "number", get: (r) => r.stops?.length || 0 },
        { key: "estimated_km", label: "Km", format: "number" },
        { key: "duration_min", label: "Süre (dk)", format: "number" },
      ],
      rows: filteredRoutes,
    };
    const detailCols = [
      { key: "stop_no", label: "Durak" },
      { key: "name", label: "Personel" },
      { key: "address", label: "Adres" },
      { key: "pickup_time", label: "Alış Saati" },
      { key: "walk_m", label: "Yürüme (m)" },
    ];
    const details = filteredRoutes.map((r, ri) => {
      const vehicle = vehicleById[r.vehicle_id]?.plate;
      const driver = driverById[r.driver_id];
      const info = [
        dirText(r.direction),
        r.shift,
        vehicle ? `Araç: ${vehicle}` : null,
        driver ? `Şoför: ${driver.name}${driver.phone ? ` (${driver.phone})` : ""}` : null,
        r.estimated_km > 0 ? `${r.estimated_km} km` : null,
        r.duration_min > 0 ? `~${r.duration_min} dk` : null,
        r.dest?.label || r.end_point ? `Varış: ${(r.dest?.label || r.end_point).slice(0, 60)}` : null,
      ].filter(Boolean).join(" · ");
      const rows = r.stops?.length > 0
        ? r.stops.flatMap((s, si) => (s.personnel || []).map((p) => ({
            stop_no: si + 1, name: p.name, address: p.address || personnelById[p.id]?.address || "",
            pickup_time: p.pickup_time || "", walk_m: p.walk_m ?? "",
          })))
        : (r.personnel_ids || []).map((pid, i) => {
            const p = personnelById[pid];
            return {
              stop_no: i + 1, name: p ? `${p.first_name} ${p.last_name}` : pid,
              address: p ? [p.district, p.address].filter(Boolean).join(" — ") : "",
              pickup_time: p?.pickup_time || "", walk_m: "",
            };
          });
      return { name: `${ri + 1}. Güzergah`, heading: `${r.name} — ${info}`, columns: detailCols, rows };
    });
    return [summary, ...details];
  }, [filteredRoutes, vehicleById, driverById, personnelById]);

  return (
    <>
      <PageHeader
        title="Taşıma Güzergahları"
        subtitle="Firmalara göre otomatik güzergah oluşturun, personelleri araç kapasitesine göre gruplandırın."
        testid="routes-header"
        actions={
          <div className="flex items-center gap-2">
            {exportSections.length > 0 && (
              <ExportButtons
                filename={`guzergah-plani${companyFilter ? `-${companyFilter.toLowerCase().replace(/[^a-z0-9ğüşıöç]+/gi, "-")}` : ""}`}
                title="Güzergah Planı"
                subtitle={`${companyFilter || "Tüm Firmalar"} · ${filteredRoutes.length} güzergah · ${filteredRoutes.reduce((a, x) => a + (x.personnel_ids?.length || 0), 0)} personel`}
                sections={exportSections}
                testidPrefix="routes-export"
              />
            )}
            <button data-testid="route-manual-btn" onClick={() => openManual(null)} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Plus className="w-3.5 h-3.5" /> Manuel Ekle
            </button>
            <button data-testid="route-auto-btn" onClick={() => setShowAuto(true)} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors">
              <Wand2 className="w-4 h-4" strokeWidth={2.2} /> Otomatik Oluştur
            </button>
          </div>
        }
      />

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="text-xs uppercase tracking-widest font-semibold text-neutral-500">Firma Filtresi:</div>
        <div className="flex gap-1.5 flex-wrap">
          <button
            data-testid="filter-all"
            onClick={() => setCompanyFilter("")}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${!companyFilter ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
          >Tümü ({routes.length})</button>
          {routeCompanies.map(c => (
            <button
              key={c}
              data-testid={`filter-company-${c}`}
              onClick={() => setCompanyFilter(c)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${companyFilter === c ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
            >{c} ({routes.filter(r => r.company_name === c).length})</button>
          ))}
        </div>
      </div>

      {Object.keys(groupedByCompany).length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] p-16 text-center">
          <MapPin className="w-10 h-10 mx-auto text-neutral-300 mb-4" strokeWidth={1.2} />
          <div className="text-neutral-500 text-sm mb-4">Henüz güzergah yok.</div>
          <button onClick={() => setShowAuto(true)} className="text-xs uppercase tracking-widest font-semibold text-[#0020C2] hover:underline">Otomatik oluştur →</button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByCompany).map(([company, list]) => (
            <div key={company} data-testid={`company-section-${company}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">Firma</div>
                  <div className="text-xl font-bold font-heading">{company}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-neutral-500">
                    <span className="num font-semibold">{list.length}</span> güzergah · <span className="num font-semibold">{list.reduce((a, x) => a + (x.personnel_ids?.length || 0), 0)}</span> personel
                  </div>
                  {/* İş başlangıç saati → durak saatlerini hesapla */}
                  <div className="flex items-center gap-1.5 border border-[#E5E5E5] pl-2 bg-white" title="Firmanın iş başlangıç saati — varıştan geriye doğru tüm durak/personel alış saatleri hesaplanır">
                    <Clock className="w-3.5 h-3.5 text-neutral-400" />
                    <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">İşbaşı</span>
                    <input
                      type="time"
                      data-testid={`company-workstart-${company}`}
                      value={workStartDraft[company] ?? companySettings[company]?.work_start ?? ""}
                      onChange={e => setWorkStartDraft(d => ({ ...d, [company]: e.target.value }))}
                      className="text-xs px-1.5 py-1.5 border-l border-[#E5E5E5] num w-[92px]"
                    />
                    <button
                      data-testid={`company-calctimes-${company}`}
                      onClick={() => calcTimes(company)}
                      className="px-2.5 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2]"
                    >
                      Saatleri Hesapla
                    </button>
                  </div>
                  {list.some(r => r.stops?.length > 0) && (
                    <button
                      data-testid={`company-map-btn-${company}`}
                      onClick={() => setCompanyMap(company)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A]"
                      title="Firmanın tüm güzergahlarını tek haritada göster"
                    >
                      <MapIcon className="w-3.5 h-3.5" /> Tümünü Haritada Gör
                    </button>
                  )}
                  <button
                    data-testid={`company-share-btn-${company}`}
                    onClick={() => shareLink({ company_name: company }, `${company} — tüm güzergah planı`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                    title="Firmanın TÜM güzergah planını tek link olarak paylaş"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Planı Paylaş
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map(r => {
                  const isOpen = expanded.has(r.id);
                  const dirLabel = r.direction === "pickup" ? "Alım" : "Bırakma";
                  const dirColor = r.direction === "pickup" ? "bg-[#16A34A]" : "bg-[#0020C2]";
                  return (
                    <div key={r.id} className="bg-white border border-[#E5E5E5] rounded-md p-4" data-testid={`route-card-${r.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 text-white ${dirColor}`}>{dirLabel}</span>
                            {r.auto_generated && <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 bg-[#F59E0B] text-white">Otomatik</span>}
                            {r.shift && <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 bg-neutral-100">{r.shift}</span>}
                          </div>
                          <div className="font-semibold text-sm leading-tight">{r.name}</div>
                          {r.group_name && <div className="text-xs text-neutral-500 mt-0.5">Bölge: {r.group_name}</div>}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button data-testid={`route-edit-${r.id}`} onClick={() => openManual(r)} className="p-1.5 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                          <button data-testid={`route-delete-${r.id}`} onClick={() => removeRoute(r)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 py-2 border-y border-[#F5F5F5] text-xs items-center">
                        <div className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 text-neutral-400" />
                          <span className="num font-semibold">{r.personnel_ids?.length || 0}</span>
                          <span className="text-neutral-500">kişi{r.capacity ? ` / ${r.capacity}` : ""}</span>
                        </div>
                        <div className="flex items-center gap-1 min-w-0">
                          <Truck className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                          <select
                            data-testid={`route-assign-vehicle-${r.id}`}
                            value={r.vehicle_id || ""}
                            onChange={e => quickAssign(r.id, "vehicle_id", e.target.value)}
                            className={`w-full text-xs border-0 bg-transparent cursor-pointer focus:outline-none ${r.vehicle_id ? "font-semibold" : "text-neutral-400"}`}
                          >
                            <option value="">Araç ata...</option>
                            {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate}</option>)}
                          </select>
                        </div>
                        <div className="flex items-center gap-1 min-w-0">
                          <MapPin className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                          <select
                            data-testid={`route-assign-driver-${r.id}`}
                            value={r.driver_id || ""}
                            onChange={e => quickAssign(r.id, "driver_id", e.target.value)}
                            className={`w-full text-xs border-0 bg-transparent cursor-pointer focus:outline-none ${r.driver_id ? "font-semibold" : "text-neutral-400"}`}
                          >
                            <option value="">Şoför ata...</option>
                            {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-2 text-xs" data-testid={`route-actions-${r.id}`}>
                        {(r.estimated_km > 0 || r.duration_min > 0) && (
                          <>
                            <span className="flex items-center gap-1 font-semibold"><RouteIcon className="w-3.5 h-3.5 text-[#0020C2]" /><span className="num">{r.estimated_km}</span> km</span>
                            {r.duration_min > 0 && <span className="flex items-center gap-1 font-semibold"><Clock className="w-3.5 h-3.5 text-[#0020C2]" /><span className="num">{r.duration_min}</span> dk</span>}
                            {r.first_stop_time && <span className="font-semibold text-[#16A34A]">İlk durak <span className="num">{r.first_stop_time}</span></span>}
                            {r.arrival_time && <span className="font-semibold text-[#DC2626]">Varış <span className="num">{r.arrival_time}</span></span>}
                            {r.stops?.length > 0 && <span className="text-neutral-500"><span className="num font-semibold">{r.stops.length}</span> durak</span>}
                          </>
                        )}
                        <div className="ml-auto flex items-center gap-1.5">
                          {r.driver_id && (
                            <button
                              data-testid={`route-tasklink-btn-${r.id}`}
                              onClick={() => sendTaskLink(r)}
                              title="Şoföre görev ekranı linkini gönder (telefondan güzergah + durak listesi)"
                              className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white"
                            >
                              <Smartphone className="w-3 h-3" /> Görev Linki
                            </button>
                          )}
                          <button
                            data-testid={`route-whatsapp-btn-${r.id}`}
                            onClick={() => sendWhatsApp(r)}
                            title="Şoföre WhatsApp ile durak listesi + navigasyon linki gönder"
                            className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold bg-[#16A34A] text-white hover:bg-[#0A0A0A]"
                          >
                            <MessageCircle className="w-3 h-3" /> WhatsApp
                          </button>
                          {r.stops?.length > 0 && (
                            <button
                              data-testid={`route-map-btn-${r.id}`}
                              onClick={() => setMapRoute(r)}
                              className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A]"
                            >
                              <MapIcon className="w-3 h-3" /> Haritada Gör
                            </button>
                          )}
                          <button
                            data-testid={`route-share-btn-${r.id}`}
                            onClick={() => shareLink({ route_id: r.id }, r.name)}
                            title="Bu güzergahı herkese açık link olarak paylaş (harita + duraklar + yürüme mesafeleri)"
                            className="flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                          >
                            <Share2 className="w-3 h-3" /> Paylaş
                          </button>
                        </div>
                      </div>

                      <button
                        data-testid={`route-expand-${r.id}`}
                        onClick={() => toggleExpand(r.id)}
                        className="mt-2 flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[#0020C2] hover:underline"
                      >
                        {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        Personel Listesi ({r.personnel_ids?.length || 0})
                      </button>

                      {isOpen && (
                        <div className="mt-3 space-y-1 border-t border-[#F5F5F5] pt-3">
                          {r.stops?.length > 0 ? (
                            r.stops.map((s, si) => (
                              <div key={si} className="py-1">
                                <div className="flex items-center gap-2 text-[11px] font-bold">
                                  <span className="w-4 h-4 bg-[#0020C2] text-white rounded-full flex items-center justify-center text-[9px] num shrink-0">{si + 1}</span>
                                  Durak {si + 1}
                                  {s.pickup_time && <span className="num text-[#0020C2]">· {s.pickup_time}</span>}
                                  <span className="text-neutral-400 font-normal">· {s.count} kişi</span>
                                </div>
                                {(s.personnel || []).map((p) => (
                                  <div key={p.id} className="flex items-center justify-between text-xs pl-6 py-0.5 gap-2">
                                    <span className="truncate">{p.name}</span>
                                    <span className="flex items-center gap-2 shrink-0">
                                      <span className={`num text-[11px] ${p.walk_m > 400 ? "text-[#DC2626]" : "text-neutral-500"}`}>{p.walk_m} m yürüme</span>
                                      <MoveSelect route={r} pid={p.id} />
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ))
                          ) : (
                            (r.personnel_ids || []).map((pid, idx) => {
                              const p = personnelById[pid];
                              if (!p) return null;
                              return (
                                <div key={pid} className="flex items-start gap-2 text-xs py-1.5">
                                  <span className="text-neutral-400 num w-5">{idx + 1}.</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium">{p.first_name} {p.last_name}</div>
                                    <div className="text-neutral-500 truncate">{p.district ? `${p.district} — ` : ""}{p.address}</div>
                                  </div>
                                  {p.pickup_time && <span className="num text-neutral-500 text-[11px] shrink-0">{p.pickup_time}</span>}
                                  <MoveSelect route={r} pid={pid} />
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Auto-generate modal */}
      {showAuto && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAuto(false)}>
          <div className="bg-white w-full max-w-lg rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Otomatik Güzergah</div>
                <div className="font-heading font-bold text-lg flex items-center gap-2"><Wand2 className="w-4 h-4 text-[#0020C2]" /> Personel Adresine Göre Grupla</div>
              </div>
              <button onClick={() => setShowAuto(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Firma *</label>
                <select data-testid="auto-company" value={autoForm.company_name} onChange={e => setAutoForm({...autoForm, company_name: e.target.value, districts: []})} className="input mt-1.5">
                  <option value="">Seçiniz</option>
                  {companies.map(c => <option key={c} value={c}>{c} ({personnel.filter(p => p.company_name === c).length} personel · {personnel.filter(p => p.company_name === c && typeof p.lat === "number").length} konumlu)</option>)}
                </select>
              </div>

              {/* Bölge / İlçe bazlı seçim */}
              {autoForm.company_name && (() => {
                const counts = {};
                personnel.filter(p => p.company_name === autoForm.company_name).forEach(p => {
                  const d = (p.district || "").trim();
                  if (d) counts[d] = (counts[d] || 0) + 1;
                });
                const list = Object.entries(counts).sort((a, b) => b[1] - a[1]);
                if (list.length === 0) return null;
                const toggle = (d) => setAutoForm(f => ({
                  ...f,
                  districts: f.districts.includes(d) ? f.districts.filter(x => x !== d) : [...f.districts, d],
                }));
                return (
                  <div data-testid="auto-districts-section">
                    <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">
                      Bölge / İlçe Seç <span className="text-neutral-400 normal-case tracking-normal">(boş bırakılırsa tüm bölgeler dahil edilir)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {list.map(([d, n]) => {
                        const on = autoForm.districts.includes(d);
                        return (
                          <button
                            key={d}
                            data-testid={`auto-district-${d}`}
                            onClick={() => toggle(d)}
                            className={`px-2.5 py-1 text-[11px] font-semibold border transition-colors ${on ? "bg-[#0020C2] text-white border-[#0020C2]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}
                          >
                            {d} <span className="num">({n})</span>
                          </button>
                        );
                      })}
                    </div>
                    {autoForm.districts.length > 0 && (
                      <div className="text-[11px] text-[#0020C2] font-semibold mt-1.5">
                        Sadece {autoForm.districts.join(", ")} bölgesindeki <span className="num">{list.filter(([d]) => autoForm.districts.includes(d)).reduce((a, [, n]) => a + n, 0)}</span> personel güzergahlanacak
                      </div>
                    )}
                  </div>
                );
              })()}

              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İşyeri Adresi (Hedef) *</label>
                <div className="flex gap-2 mt-1.5">
                  <input
                    data-testid="auto-dest-input"
                    value={destQuery}
                    onChange={e => setDestQuery(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && searchDest()}
                    placeholder="örn: Sincan OSB, Ankara"
                    className="input flex-1"
                  />
                  <button data-testid="auto-dest-search" onClick={searchDest} disabled={destSearching} className="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
                    {destSearching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Bul
                  </button>
                </div>
                {destResults.length > 0 && (
                  <div className="border border-[#E5E5E5] divide-y divide-[#F5F5F5] max-h-[110px] overflow-y-auto mt-2">
                    {destResults.map((r, i) => {
                      const active = dest && dest.lat === r.lat && dest.lng === r.lng;
                      return (
                        <button key={i} data-testid={`auto-dest-result-${i}`} onClick={() => setDest({ lat: r.lat, lng: r.lng, label: r.label })}
                                className={`w-full flex items-start gap-2 px-3 py-1.5 text-left text-xs hover:bg-[#F5F8FF] ${active ? "bg-[#F5F8FF] border-l-2 border-l-[#0020C2] font-semibold" : ""}`}>
                          <MapPin className={`w-3 h-3 shrink-0 mt-0.5 ${active ? "text-[#0020C2]" : "text-neutral-400"}`} />
                          {r.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {dest && <div className="text-[11px] text-[#16A34A] mt-1.5 font-semibold">✓ Hedef seçildi: <span className="font-normal text-neutral-600">{dest.label.slice(0, 80)}</span></div>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Araç Kapasiteleri *</label>
                  <input data-testid="auto-capacities" value={autoForm.capacities} onChange={e => setAutoForm({...autoForm, capacities: e.target.value})} placeholder="16,27" className="input mt-1.5 num" />
                  <div className="text-[10px] text-neutral-500 mt-1">Virgülle ayırın — örn: 16,16,27 (iki minibüs + bir midibüs)</div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Durak Birleştirme (metre)</label>
                  <input data-testid="auto-merge" type="number" min="50" step="50" value={autoForm.stop_merge_m} onChange={e => setAutoForm({...autoForm, stop_merge_m: e.target.value})} className="input mt-1.5 num" />
                  <div className="text-[10px] text-neutral-500 mt-1">Bu mesafedeki personeller aynı durağı paylaşır</div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yön</label>
                  <select value={autoForm.direction} onChange={e => setAutoForm({...autoForm, direction: e.target.value})} className="input mt-1.5">
                    <option value="pickup">Alım (İşe götürme)</option>
                    <option value="dropoff">Bırakma (Evden dönüş)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vardiya (opsiyonel)</label>
                  <input placeholder="Sabah/Akşam" value={autoForm.shift} onChange={e => setAutoForm({...autoForm, shift: e.target.value})} className="input mt-1.5" />
                </div>
              </div>

              <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3 text-xs text-neutral-600">
                <strong>Nasıl çalışır:</strong> Yakın personeller aynı durakta birleştirilir, duraklar ana yola oturtulur, araçlar kapasiteye göre doldurulur (yakınlar aynı araçta), her güzergahın gerçek yol km&apos;si ve süresi hesaplanır. Personelin durağa yürüme mesafesi de gösterilir. <span className="text-[#D97706] font-semibold">Sadece konumlanmış personeller dahil edilir.</span>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button onClick={() => setShowAuto(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="auto-submit-btn" onClick={runAuto} disabled={planning} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
                  {planning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} {planning ? "Hesaplanıyor..." : "Güzergahları Oluştur"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual form modal */}
      {showManual && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowManual(false)}>
          <div className="bg-white w-full max-w-3xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">Güzergah</div>
              </div>
              <button onClick={() => setShowManual(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Güzergah Adı *</label>
                  <input required value={manualForm.name} onChange={e => setManualForm({...manualForm, name: e.target.value})} className="input mt-1.5" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Firma *</label>
                  <select value={manualForm.company_name} onChange={e => setManualForm({...manualForm, company_name: e.target.value})} className="input mt-1.5">
                    <option value="">Seçiniz</option>
                    {companies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yön</label>
                  <select value={manualForm.direction} onChange={e => setManualForm({...manualForm, direction: e.target.value})} className="input mt-1.5">
                    <option value="pickup">Alım</option>
                    <option value="dropoff">Bırakma</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Araç</label>
                  <select value={manualForm.vehicle_id} onChange={e => setManualForm({...manualForm, vehicle_id: e.target.value})} className="input mt-1.5">
                    <option value="">Seçiniz</option>
                    {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate} — {v.model}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Şoför</label>
                  <select value={manualForm.driver_id} onChange={e => setManualForm({...manualForm, driver_id: e.target.value})} className="input mt-1.5">
                    <option value="">Seçiniz</option>
                    {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Başlangıç Noktası</label>
                  <input value={manualForm.start_point} onChange={e => setManualForm({...manualForm, start_point: e.target.value})} className="input mt-1.5" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Bitiş Noktası</label>
                  <input value={manualForm.end_point} onChange={e => setManualForm({...manualForm, end_point: e.target.value})} className="input mt-1.5" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tahmini Km</label>
                  <input type="number" step="any" value={manualForm.estimated_km} onChange={e => setManualForm({...manualForm, estimated_km: e.target.value})} className="input mt-1.5 num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vardiya</label>
                  <input value={manualForm.shift} onChange={e => setManualForm({...manualForm, shift: e.target.value})} className="input mt-1.5" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Personel Seç ({manualForm.personnel_ids.length})</label>
                  {manualForm.company_name && (
                    <span className="text-[10px] text-neutral-500">{personnel.filter(p => p.company_name === manualForm.company_name).length} müsait</span>
                  )}
                </div>
                <div className="border border-[#E5E5E5] max-h-64 overflow-y-auto">
                  {personnel.filter(p => !manualForm.company_name || p.company_name === manualForm.company_name).map(p => {
                    const checked = manualForm.personnel_ids.includes(p.id);
                    return (
                      <label key={p.id} className="flex items-center gap-3 px-3 py-2 border-b border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer">
                        <input type="checkbox" checked={checked} onChange={() => {
                          setManualForm(f => ({
                            ...f,
                            personnel_ids: checked ? f.personnel_ids.filter(x => x !== p.id) : [...f.personnel_ids, p.id]
                          }));
                        }} />
                        <div className="flex-1 text-xs">
                          <div className="font-medium">{p.first_name} {p.last_name}</div>
                          <div className="text-neutral-500">{p.district} — {p.address}</div>
                        </div>
                        <span className="text-[10px] text-neutral-500 num">{p.pickup_time}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Harita & Duraklar */}
              <div className="border border-[#E5E5E5] p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="manual-map-section">
                <div className="text-xs">
                  <div className="font-semibold uppercase tracking-widest text-neutral-600 mb-0.5">Harita &amp; Duraklar</div>
                  {manualForm.stops?.length > 0 ? (
                    <div className="text-[#16A34A] font-semibold">
                      ✓ <span className="num">{manualForm.stops.length}</span> durak · <span className="num">{manualForm.estimated_km}</span> km{manualForm.duration_min ? <> · ~<span className="num">{manualForm.duration_min}</span> dk</> : null}
                      <span className="text-neutral-500 font-normal"> — personel yürüme mesafeleri durak-adres arası hesaplandı</span>
                    </div>
                  ) : (
                    <div className="text-neutral-500">Haritada kendi duraklarınızı işaretleyin — personeller en yakın durağa atanır, yürüme mesafesi hesaplanır.</div>
                  )}
                </div>
                <button
                  data-testid="manual-map-btn"
                  onClick={openStopBuilder}
                  className="flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A]"
                >
                  <MapIcon className="w-3.5 h-3.5" /> {manualForm.stops?.length > 0 ? "Haritayı Düzenle" : "Haritada Oluştur"}
                </button>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Notlar</label>
                <textarea rows={2} value={manualForm.notes} onChange={e => setManualForm({...manualForm, notes: e.target.value})} className="input mt-1.5" />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button onClick={() => setShowManual(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button onClick={submitManual} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Haritada durak oluşturucu (manuel güzergah) */}
      {showStopBuilder && (
        <StopBuilderMapModal
          people={manualForm.personnel_ids.map(id => personnelById[id]).filter(Boolean)}
          initialStops={manualForm.stops || []}
          initialDest={manualForm.dest || null}
          onApply={applyMapData}
          onClose={() => setShowStopBuilder(false)}
        />
      )}
      {/* Güzergah harita modalı */}
      {mapRoute && <RouteMapModal route={mapRoute} onStopMove={moveStop} onClose={() => setMapRoute(null)} />}
      {/* Firma bazlı toplu harita */}
      {companyMap && (
        <CompanyRoutesMapModal
          company={companyMap}
          routes={routes.filter(r => r.company_name === companyMap)}
          vehicleById={vehicleById}
          driverById={driverById}
          unassigned={personnel.filter(p =>
            p.company_name === companyMap &&
            !routes.some(r => r.company_name === companyMap && (r.personnel_ids || []).includes(p.id))
          )}
          onAdd={addPersonnelToRoute}
          onCreateRoute={(payload) => createRouteOnMap(companyMap, payload)}
          onMove={movePersonnel}
          onStopMove={moveStop}
          onRename={renameRoute}
          onClose={() => setCompanyMap(null)}
        />
      )}
    </>
  );
}
